// Standalone "Filters" page reachable from Video Analysis's toolbar (next to
// the existing Filters funnel icon) — a MultiChoice-driven alternative to the
// react-select VideoFilters.jsx modal. 3 columns: (1) filters, (2) the same
// EventsList.jsx component Video Analysis itself uses (driven here via its
// new `filterFn` predicate prop instead of the modal's `filters` shape), (3)
// a video player that seeks to the selected event, same pattern as
// MomentumChartPage.jsx/ServeReceiveByZones.jsx (no live-HLS-drift-watchdog —
// this is a browse/review page, not a live broadcast view).
//
// SPEC AMBIGUITIES RESOLVED (flag to user, may need correction):
//   - "Phases" (Side out / Transition) is derived per RALLY (grouped by
//     TeamScore/OppositionScore boundaries), not per team: every event up to
//     and including the rally's first Attack (either side) is "Side out";
//     everything after is "Transition" — mirrors the same convention already
//     established for Serve Receive By Zones' Stages filter, generalised to
//     the whole rally instead of just the receiving team's own attacks.
//   - "Score ranges" buckets the point differential (|TeamScore -
//     OppositionScore|) at the moment of each event into 3 fixed bands:
//     Close (0-2), Moderate (3-5), Lopsided (6+).
//   - "Set winners" classifies by which side won the FINAL score of the set
//     the event belongs to (set.HomeScore/AwayScore), normalised to
//     Focus/Oppositions the same way every other side-aware filter here is.
//   - Several new UI strings here are English-fallback only for now (not yet
//     added to the 15 non-English translations.js dicts) — this is a large
//     new feature; flag if full translation is wanted.
import { ChevronLeftIcon as ChevronLeftMiniIcon, ChevronRightIcon as ChevronRightMiniIcon } from "@heroicons/react/16/solid";
import {
    ArrowLeftCircleIcon,
    ArrowPathIcon,
    ArrowRightCircleIcon,
    BackwardIcon,
    CheckIcon,
    ChevronDoubleDownIcon,
    ChevronDoubleLeftIcon,
    ChevronDoubleRightIcon,
    ChevronDoubleUpIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    ForwardIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";
import Hls from "hls.js";
import { useEffect, useMemo, useRef, useState } from "react";
import ReactPlayer from "react-player";
import { useLocation, useNavigate } from "react-router-dom";
import MultiChoiceCompact, { selectAllItems, selectNoneItems } from "../components/layout/MultiChoiceCompact";
import EventsList from "../components/matches/EventsList";
import {
    getJumpHeightBucketLabels,
    jumpHeightBucketIndex,
    kSkillBlock,
    kSkillDefense,
    kSkillFreeball,
    kSkillPass,
    kSkillServe,
    kSkillSet,
    kSkillSettersCall,
    kSkillSpike,
    kSubstitution,
} from "../components/utils/Constants";
import { DVEventString } from "../components/utils/DVWFile";
import { commonSideForMatch, findCommonTeamName } from "../components/utils/MatchCSV";
import { getTimingForEvent, toHlsStreamUrl } from "../components/utils/Utils";
import { useLanguage } from "../context/Language/LanguageContext";

const kTrackedSkills = [kSkillServe, kSkillPass, kSkillSet, kSkillSpike, kSkillBlock, kSkillDefense, kSkillFreeball];
const kSkillLabels = {
  [kSkillServe]: "Serve",
  [kSkillPass]: "Reception",
  [kSkillSet]: "Setter Call",
  [kSkillSpike]: "Attack",
  [kSkillBlock]: "Block",
  [kSkillDefense]: "Dig",
  [kSkillFreeball]: "Freeball",
};
const kDVResults = ["=", "/", "-", "!", "+", "#"];
// Short-code dimensions shared across the per-skill sub-filters below. NOTE:
// "Type" position 6 is "N" per explicit user spec (the raw DV SubEvent scale
// this maps to -- kSpikeF=6 in Constants.js -- is labelled "Fast" elsewhere
// in the app; flagged here in case "N" was meant to be "F").
const kTypeCodes = ["H", "M", "Q", "T", "U", "N", "O"]; // ev.SubEvent 1-7
const kStrengthCodes = ["H", "P", "T"]; // ev.ExtraCode1
const kBlocksCodes = ["0", "1", "2", "3", "4"]; // ev.ExtraCode2
const kResultsCodes = ["S", "O", "F", "X", "N"]; // ev.ExtraCode3
const kScoreRangeLabels = ["Close (0-2)", "Moderate (3-5)", "Lopsided (6+)"];
// Assumed 30fps for the transport toolbar's single-frame rewind/forward step.
const FRAME_SECONDS = 1 / 30;
// Per-skill sub-filter dimensions (render order) + which buildSkillFilters
// object key each one lives under, and how many MultiChoiceCompact columns
// each dimension's short-code list uses.
const kSkillFilterDims = {
  [kSkillServe]: ["type", "grade"],
  [kSkillPass]: ["type", "grade"],
  [kSkillSet]: ["calls", "grade"],
  [kSkillSpike]: ["type", "strength", "combo", "blocks", "results", "grade"],
  [kSkillBlock]: ["type", "strength", "grade"],
  [kSkillDefense]: ["grade"],
  [kSkillFreeball]: ["grade"],
};
const kDimToFilterKey = {
  type: "typeFilter",
  strength: "strengthFilter",
  combo: "comboFilter",
  blocks: "blocksFilter",
  results: "resultsFilter",
  calls: "callsFilter",
  grade: "gradeFilter",
};
const kDimColumns = { type: 4, strength: 3, combo: 3, blocks: 5, results: 5, grade: 6, calls: 3 };

function isHomeEvent(ev) {
  if (ev.dvString !== undefined) return ev.dvString[0] === "*";
  return ev.homePlayer === true;
}

function playerKey(pl) {
  if (!pl) return null;
  return pl.shirtNumber + ". " + pl.FirstName + " " + pl.LastName.toUpperCase();
}

function makeFilter(title, items) {
  return { title, items, singleSelection: false, allselected: true, selectedValues: items.map((i) => i.name) };
}

function matchKey(match) {
  return match?.guid ?? match?.dvstring ?? match?.Guid ?? "";
}

function setKeyFor(match, gameNumber) {
  return `${matchKey(match)}-${gameNumber}`;
}

// A standalone Setter's Call event (EventType 20, scout skill char "E") is
// semantically part of the Set skill, even though DVWFile.js gives it its
// own EventType distinct from kSkillSet -- fold it into the Set panel.
function skillKeyForEvent(ev) {
  return ev.EventType === kSkillSettersCall ? kSkillSet : ev.EventType;
}

// Which normalised side ("A" = focus team, "B" = opposition) an event
// belongs to, or null if it can't be attributed to a match at all.
function normSideForEvent(ev, commonName, single) {
  const match = ev.Drill?.match;
  if (!match) return null;
  const commonSide = commonSideForMatch(match, commonName) ?? (single ? "A" : null);
  const focusSide = commonSide ?? "A";
  const side = isHomeEvent(ev) ? "A" : "B";
  return side === focusSide ? "A" : "B";
}

function buildTeamsFilter(matches) {
  const single = matches.length === 1;
  const m0 = matches[0];
  const focusLabel = single ? m0?.teamA?.Name ?? "Team A" : "Focus Team";
  const oppLabel = single ? m0?.teamB?.Name ?? "Team B" : "Oppositions";
  return makeFilter("Teams", [
    { name: focusLabel, selected: true, __side: "A" },
    { name: oppLabel, selected: true, __side: "B" },
  ]);
}

// Same single-match-vs-multi-match naming convention as buildTeamsFilter
// above: real team names when there's only one match, generic Focus
// Team/Oppositions labels otherwise. __side drives the actual gating in
// passesEvent so label text never has to be matched directly.
function buildSetWinnerFilter(matches) {
  const single = matches.length === 1;
  const m0 = matches[0];
  const focusLabel = single ? `${m0?.teamA?.Name ?? "Team A"} Won` : "Focus Team Won";
  const oppLabel = single ? `${m0?.teamB?.Name ?? "Team B"} Won` : "Oppositions Won";
  return makeFilter("Set Winner", [
    { name: focusLabel, selected: true, __side: "A" },
    { name: oppLabel, selected: true, __side: "B" },
  ]);
}

// Players depend on the CURRENT Teams selection — only players with at
// least one event, on whichever side(s) are currently selected.
function buildPlayersFilter(matches, commonName, selectedSides) {
  const single = matches.length === 1;
  const players = new Set();
  for (const match of matches) {
    const commonSide = commonSideForMatch(match, commonName) ?? (single ? "A" : null);
    const focusSide = commonSide ?? "A";
    for (const set of match.sets) {
      for (const ev of set.events) {
        if (!ev.Player) continue;
        const side = isHomeEvent(ev) ? "A" : "B";
        const norm = side === focusSide ? "A" : "B";
        if (!selectedSides.has(norm)) continue;
        const key = playerKey(ev.Player);
        if (key) players.add(key);
      }
    }
  }
  return makeFilter(
    "Players",
    [...players].sort().map((name) => ({ name, selected: true }))
  );
}

function buildSetsFilter(matches) {
  const sets = new Set();
  for (const match of matches) {
    for (const set of match.sets) sets.add(set.GameNumber);
  }
  return makeFilter(
    "Sets",
    [...sets].sort((a, b) => a - b).map((n) => ({ name: String(n), selected: true }))
  );
}

// Each tracked skill gets its own panel of sub-filters per kSkillFilterDims.
// "Setter Calls" values are scanned from both the Setter's Call events
// themselves (EventType 20) and Attack events' duplicated ev.settersCall
// field, but the resulting filter is hosted on the Set panel, since a
// setter's call belongs to the set action.
function buildSkillFilters(matches) {
  const calls = new Set();
  const combos = new Set();
  for (const match of matches) {
    for (const set of match.sets) {
      for (const ev of set.events) {
        if (ev.EventType !== kSkillSpike && ev.EventType !== kSkillSettersCall) continue;
        const call = (ev.settersCall ?? "").substring(0, 2);
        if (call) calls.add(call);
        if (ev.attackCombo && ev.attackCombo !== "~~") combos.add(ev.attackCombo);
      }
    }
  }
  const skills = {};
  for (const skillNo of kTrackedSkills) {
    const dims = kSkillFilterDims[skillNo];
    const sf = {};
    if (dims.includes("type")) sf.typeFilter = makeFilter("Type", kTypeCodes.map((name) => ({ name, selected: true })));
    if (dims.includes("strength")) sf.strengthFilter = makeFilter("Strength", kStrengthCodes.map((name) => ({ name, selected: true })));
    if (dims.includes("combo")) sf.comboFilter = makeFilter("Attack Combo", ["~", ...[...combos].sort()].map((name) => ({ name, selected: true })));
    if (dims.includes("blocks")) sf.blocksFilter = makeFilter("Blocks", kBlocksCodes.map((name) => ({ name, selected: true })));
    if (dims.includes("results")) sf.resultsFilter = makeFilter("Results", kResultsCodes.map((name) => ({ name, selected: true })));
    if (dims.includes("calls")) sf.callsFilter = makeFilter("Setter Calls", ["~", ...[...calls].sort()].map((name) => ({ name, selected: true })));
    if (dims.includes("grade")) sf.gradeFilter = makeFilter("Grade", kDVResults.map((name) => ({ name, selected: true })));
    skills[skillNo] = sf;
  }
  return skills;
}

// Groups each set's events into rallies (TeamScore/OppositionScore change
// boundaries) and tags every event with "SIDE OUT" (up to and including the
// rally's first Attack) or "TRANSITION" (everything after).
function computeEventPhases(matches) {
  const phaseByEvent = new Map();
  for (const match of matches) {
    for (const set of match.sets) {
      let rally = [];
      let prevKey = null;
      const flush = () => {
        if (rally.length === 0) return;
        const firstAttackIdx = rally.findIndex((ev) => ev.EventType === kSkillSpike);
        rally.forEach((ev, idx) => {
          phaseByEvent.set(ev, firstAttackIdx === -1 || idx <= firstAttackIdx ? "SIDE OUT" : "TRANSITION");
        });
        rally = [];
      };
      for (const ev of set.events) {
        const key = `${ev.TeamScore ?? 0}-${ev.OppositionScore ?? 0}`;
        if (prevKey !== null && key !== prevKey) flush();
        rally.push(ev);
        prevKey = key;
      }
      flush();
    }
  }
  return phaseByEvent;
}

function computeSetWinners(matches) {
  const map = new Map();
  for (const match of matches) {
    for (const set of match.sets) {
      const home = set.HomeScore ?? 0;
      const away = set.AwayScore ?? 0;
      let winner = null;
      if (home > away) winner = "A";
      else if (away > home) winner = "B";
      map.set(setKeyFor(match, set.GameNumber), winner);
    }
  }
  return map;
}

function VideoAnalysisFiltersPage() {
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const { matches, team } = location.state ?? {};

  const [filterVersion, setFilterVersion] = useState(0);
  const onFilterOptionChanged = () => setFilterVersion((v) => v + 1);

  // Master per-skill on/off switch (independent of the sub-filter selections
  // within each panel) -- an event whose skill isn't in this set never
  // passes, regardless of its Type/Grade/etc. selections.
  const [enabledSkills, setEnabledSkills] = useState(() => new Set(kTrackedSkills));
  const toggleSkillEnabled = (skillNo) =>
    setEnabledSkills((prev) => {
      const next = new Set(prev);
      if (next.has(skillNo)) next.delete(skillNo);
      else next.add(skillNo);
      return next;
    });
  const enableAllSkills = () => setEnabledSkills(new Set(kTrackedSkills));
  const disableAllSkills = () => setEnabledSkills(new Set());

  // Free-text, case-insensitive substring search over ev.customCode (the
  // DVW char-16+ / VSM "custom" field) -- not a MultiChoice, just a text box.
  const [customCodeSearch, setCustomCodeSearch] = useState("");

  const commonName = useMemo(() => findCommonTeamName(matches ?? []), [matches]);
  const phaseByEvent = useMemo(() => computeEventPhases(matches ?? []), [matches]);
  const setWinnerByKey = useMemo(() => computeSetWinners(matches ?? []), [matches]);

  const teamsFilter = useMemo(() => buildTeamsFilter(matches ?? []), [matches]);
  // filterVersion isn't read directly below, but MUST stay in the deps array
  // -- MultiChoice mutates teamsFilter.items[i].selected IN PLACE, so without
  // it this memo would never recompute when the Teams selection changes.
  const selectedSidesKey = useMemo(
    () => teamsFilter.items.filter((i) => i.selected).map((i) => i.__side).sort().join(","),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [teamsFilter, filterVersion]
  );
  const playersFilter = useMemo(
    () => buildPlayersFilter(matches ?? [], commonName, new Set(selectedSidesKey.split(",").filter(Boolean))),
    [matches, commonName, selectedSidesKey]
  );
  const skillFilters = useMemo(() => buildSkillFilters(matches ?? []), [matches]);
  const setsFilter = useMemo(() => buildSetsFilter(matches ?? []), [matches]);
  const [rotationsFilter] = useState(() =>
    makeFilter("Rotations", ["1", "2", "3", "4", "5", "6"].map((name) => ({ name, selected: true })))
  );
  const [phasesFilter] = useState(() => makeFilter("Phases", [{ name: "SIDE OUT", selected: true }, { name: "TRANSITION", selected: true }]));
  const [scoreRangeFilter] = useState(() => makeFilter("Score Range", kScoreRangeLabels.map((name) => ({ name, selected: true }))));
  const setWinnerFilter = useMemo(() => buildSetWinnerFilter(matches ?? []), [matches]);
  const [jumpHeightFilter] = useState(() => makeFilter("Jump Height", getJumpHeightBucketLabels().map((name) => ({ name, selected: true }))));

  const resetAllFilters = () => {
    const boxes = [
      teamsFilter,
      playersFilter,
      setsFilter,
      rotationsFilter,
      phasesFilter,
      scoreRangeFilter,
      setWinnerFilter,
      jumpHeightFilter,
      ...kTrackedSkills.flatMap((sk) => Object.values(skillFilters[sk])),
    ];
    for (const box of boxes) {
      for (const item of box.items) item.selected = true;
      box.allselected = true;
    }
    setEnabledSkills(new Set(kTrackedSkills));
    setCustomCodeSearch("");
    setFilterVersion((v) => v + 1);
  };

  const passesEvent = useMemo(() => {
    const single = (matches ?? []).length === 1;
    const customCodeTerms = customCodeSearch
      .split(",")
      .map((term) => term.trim().toLowerCase())
      .filter(Boolean);
    const selSides = new Set(teamsFilter.items.filter((i) => i.selected).map((i) => i.__side));
    const selPlayers = new Set(playersFilter.items.filter((i) => i.selected).map((i) => i.name));
    const selSets = new Set(setsFilter.items.filter((i) => i.selected).map((i) => i.name));
    const selRotations = new Set(rotationsFilter.items.filter((i) => i.selected).map((i) => i.name));
    const selPhases = new Set(phasesFilter.items.filter((i) => i.selected).map((i) => i.name));
    const selScoreRanges = new Set(scoreRangeFilter.items.filter((i) => i.selected).map((i) => i.name));
    const selSetWinners = new Set(setWinnerFilter.items.filter((i) => i.selected).map((i) => i.__side));
    const jumpHeightLabels = getJumpHeightBucketLabels();
    const selJumpHeights = new Set(jumpHeightFilter.items.filter((i) => i.selected).map((i) => i.name));
    const jumpHeightNarrowed = selJumpHeights.size < jumpHeightLabels.length;
    const selSetFor = (filter) => new Set(filter.items.filter((i) => i.selected).map((i) => i.name));
    const selectedByDim = {};
    for (const sk of kTrackedSkills) {
      const sf = skillFilters[sk];
      selectedByDim[sk] = {
        type: sf.typeFilter && selSetFor(sf.typeFilter),
        strength: sf.strengthFilter && selSetFor(sf.strengthFilter),
        combo: sf.comboFilter && selSetFor(sf.comboFilter),
        blocks: sf.blocksFilter && selSetFor(sf.blocksFilter),
        results: sf.resultsFilter && selSetFor(sf.resultsFilter),
        calls: sf.callsFilter && selSetFor(sf.callsFilter),
        grade: sf.gradeFilter && selSetFor(sf.gradeFilter),
      };
    }

    return (ev) => {
      if (ev.EventType === kSubstitution) return false;

      const norm = normSideForEvent(ev, commonName, single);
      if (norm == null || !selSides.has(norm)) return false;

      if (ev.Player) {
        const key = playerKey(ev.Player);
        if (key && !selPlayers.has(key)) return false;
      }

      const gameNumber = ev.Drill?.GameNumber;
      if (gameNumber != null && !selSets.has(String(gameNumber))) return false;

      if (ev.Row != null && ev.Row > 0 && !selRotations.has(String(ev.Row))) return false;

      const phase = phaseByEvent.get(ev);
      if (phase && !selPhases.has(phase)) return false;

      const diff = Math.abs((ev.TeamScore ?? 0) - (ev.OppositionScore ?? 0));
      const bucket = diff <= 2 ? kScoreRangeLabels[0] : diff <= 5 ? kScoreRangeLabels[1] : kScoreRangeLabels[2];
      if (!selScoreRanges.has(bucket)) return false;

      const match = ev.Drill?.match;
      if (match && gameNumber != null) {
        const winnerSide = setWinnerByKey.get(setKeyFor(match, gameNumber));
        if (winnerSide) {
          const commonSide = commonSideForMatch(match, commonName) ?? (single ? "A" : null);
          const focusSide = commonSide ?? "A";
          const normWinnerSide = winnerSide === focusSide ? "A" : "B";
          if (!selSetWinners.has(normWinnerSide)) return false;
        }
      }

      const dimSel = selectedByDim[skillKeyForEvent(ev)];
      if (dimSel) {
        if (!enabledSkills.has(skillKeyForEvent(ev))) return false;

        if (dimSel.grade && ev.DVGrade !== undefined && !dimSel.grade.has(ev.DVGrade)) return false;

        if (dimSel.type) {
          const idx = Number.parseInt(ev.SubEvent, 10);
          if (Number.isInteger(idx) && idx >= 1 && idx <= kTypeCodes.length && !dimSel.type.has(kTypeCodes[idx - 1])) return false;
        }

        if (dimSel.strength && kStrengthCodes.includes(ev.ExtraCode1) && !dimSel.strength.has(ev.ExtraCode1)) return false;

        if (dimSel.combo) {
          const combo = ev.attackCombo && ev.attackCombo !== "~~" ? ev.attackCombo : "~";
          if (!dimSel.combo.has(combo)) return false;
        }

        if (dimSel.blocks && kBlocksCodes.includes(ev.ExtraCode2) && !dimSel.blocks.has(ev.ExtraCode2)) return false;

        if (dimSel.results && kResultsCodes.includes(ev.ExtraCode3) && !dimSel.results.has(ev.ExtraCode3)) return false;

        if (dimSel.calls) {
          const call = (ev.settersCall ?? "").substring(0, 2) || "~";
          if (!dimSel.calls.has(call)) return false;
        }
      }

      if (customCodeTerms.length > 0) {
        const code = (ev.customCode ?? "").toLowerCase();
        if (!customCodeTerms.some((term) => code.includes(term))) return false;
      }

      // Only actively excludes events once the user narrows this filter
      // (default "all buckets selected" lets every event through, jump data
      // or not) -- once narrowed, an event with no synced jump height can't
      // match any bucket, so it's excluded.
      if (jumpHeightNarrowed) {
        if (ev.imaHeightM == null) return false;
        const idx = jumpHeightBucketIndex(ev.imaHeightM);
        if (idx == null || !selJumpHeights.has(jumpHeightLabels[idx])) return false;
      }

      return true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    matches,
    commonName,
    teamsFilter,
    playersFilter,
    skillFilters,
    enabledSkills,
    setsFilter,
    rotationsFilter,
    phasesFilter,
    scoreRangeFilter,
    setWinnerFilter,
    jumpHeightFilter,
    customCodeSearch,
    filterVersion,
    phaseByEvent,
    setWinnerByKey,
  ]);

  // Video player wiring — same pattern as MomentumChartPage.jsx/
  // ServeReceiveByZones.jsx (YouTube / HLS / plain file via ReactPlayer,
  // isReady/pendingSeekRef queuing, currentMatchRef match-switch for
  // multi-match reports), simpler than VideoAnalysis.jsx's own full
  // live-stream drift-watchdog version (not needed for a browse/review page).
  const playerRef = useRef();
  const hlsVideoRef = useRef(null);
  const hlsInstanceRef = useRef(null);
  const currentMatchRef = useRef(null);
  const pendingSeekRef = useRef(null);
  const [videoFilePath, setVideoFilePath] = useState(null);
  const [isYoutube, setIsYoutube] = useState(false);
  const [isReady, setIsReady] = useState(false);
  // Tracks the player's REAL play/pause state (incl. pauses made via the
  // native controls) so ReactPlayer's `playing` prop -- and therefore the
  // underlying <video>'s `autoplay` attribute -- doesn't silently resume a
  // paused video after a seek forces a rebuffer (seen with some CDN-hosted
  // mp4s that don't serve byte-range requests as cleanly as YouTube).
  const [isPlaying, setIsPlaying] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [collapseAll, setCollapseAll] = useState(false);
  // Flat, in-order list of currently-visible events (from EventsList's own
  // onFilter callback) -- drives Previous/Next Clip navigation below.
  const [filteredEvents, setFilteredEvents] = useState([]);

  useEffect(() => {
    if (!videoFilePath?.endsWith(".m3u8")) return;
    const vid = hlsVideoRef.current;
    if (!vid) return;
    if (hlsInstanceRef.current) {
      hlsInstanceRef.current.destroy();
      hlsInstanceRef.current = null;
    }
    if (!Hls.isSupported()) {
      vid.src = videoFilePath;
    } else {
      const hls = new Hls({ xhrSetup: (xhr) => { xhr.withCredentials = false; } });
      hls.loadSource(videoFilePath);
      hls.attachMedia(vid);
      hlsInstanceRef.current = hls;
    }
    return () => {
      if (hlsInstanceRef.current) {
        hlsInstanceRef.current.destroy();
        hlsInstanceRef.current = null;
      }
    };
  }, [videoFilePath]);

  const expandYouTubeUrl = (str) => {
    if (!str || !str.includes("youtu.be")) return { url: str, isYoutube: false };
    const m = /.*\/([0-9a-zA-Z-_]*)/.exec(str);
    if (m?.[1]) return { url: "https://www.youtube.com/watch?v=" + m[1], isYoutube: true };
    return { url: str, isYoutube: false };
  };

  const loadMatchVideo = (match) => {
    let onlineUrl = match.videoOnlineUrl;
    if (!onlineUrl) {
      const prefix = match.app === "VBStats" ? match.Guid : match.dvstring;
      try {
        const vobj = JSON.parse(localStorage.getItem(prefix + "_videoInfo"));
        if (vobj?.matchDVString === match.dvstring) onlineUrl = vobj.videoOnlineUrl;
      } catch {
        /* no cached video info for this match */
      }
    }
    if (!onlineUrl) {
      setVideoFilePath(null);
      return;
    }
    const { url, isYoutube: yt } = expandYouTubeUrl(onlineUrl);
    setIsYoutube(yt);
    setVideoFilePath(toHlsStreamUrl(url));
    setIsPlaying(true);
  };

  useEffect(() => {
    const first = matches?.[0];
    if (!first) return;
    currentMatchRef.current = first;
    setIsReady(false);
    loadMatchVideo(first);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matches]);

  const performSeek = (pos) => {
    if (videoFilePath?.endsWith(".m3u8")) {
      const vid = hlsVideoRef.current;
      if (vid) {
        vid.currentTime = pos;
        vid.play().catch(() => {});
      }
      return;
    }
    if (isYoutube) {
      const yt = playerRef.current?.getInternalPlayer();
      yt?.seekTo?.(pos, true);
      yt?.playVideo?.();
      return;
    }
    const vid = playerRef.current?.getInternalPlayer();
    if (vid && vid.currentTime !== undefined) {
      vid.currentTime = pos;
      vid.play().catch(() => {});
    } else {
      playerRef.current?.seekTo(pos, "seconds");
    }
  };

  const seekToAndPlay = (seconds) => {
    if (seconds == null || !isFinite(seconds)) return;
    const pos = Math.max(0, seconds);
    if (!isReady) {
      pendingSeekRef.current = pos;
      return;
    }
    performSeek(pos);
  };

  const handlePlayerReady = () => {
    setIsReady(true);
    if (pendingSeekRef.current != null) {
      const pos = pendingSeekRef.current;
      pendingSeekRef.current = null;
      performSeek(pos);
    }
  };

  const getCurrentPlaybackTime = () => {
    if (videoFilePath?.endsWith(".m3u8")) return hlsVideoRef.current?.currentTime ?? 0;
    return playerRef.current?.getCurrentTime?.() ?? 0;
  };

  // Same seek logic as performSeek but WITHOUT forcing playback -- used by
  // the transport toolbar so a paused video stays paused after a
  // rewind/forward nudge instead of always resuming playback.
  const seekWithoutForcingPlay = (pos) => {
    if (videoFilePath?.endsWith(".m3u8")) {
      const vid = hlsVideoRef.current;
      if (vid) vid.currentTime = pos;
      return;
    }
    if (isYoutube) {
      // YouTube's own seekTo already preserves whatever play/pause state the
      // player was already in, so no playVideo() call is needed here.
      playerRef.current?.getInternalPlayer()?.seekTo?.(pos, true);
      return;
    }
    const vid = playerRef.current?.getInternalPlayer();
    if (vid && vid.currentTime !== undefined) {
      vid.currentTime = pos;
    } else {
      playerRef.current?.seekTo(pos, "seconds");
    }
  };

  const seekRelative = (delta) => seekWithoutForcingPlay(Math.max(0, getCurrentPlaybackTime() + delta));

  const doSelectEvent = (ev) => {
    setSelectedEvent(ev);
    const match = ev.Drill?.match;
    if (!match) return;
    const tm = getTimingForEvent(ev);
    let vt = null;
    if (ev.VideoPosition !== undefined && ev.VideoPosition !== 0) {
      vt = ev.VideoPosition - tm.leadTime;
    } else if (match.videoStartTimeSeconds != null && ev.TimeStamp) {
      const secondsSinceEpoch = Math.round(ev.TimeStamp.getTime() / 1000);
      const offset = match.videoOffset != null && match.videoOffset >= 0 ? match.videoOffset : 0;
      vt = secondsSinceEpoch - match.videoStartTimeSeconds + offset - tm.leadTime;
    }
    if (match !== currentMatchRef.current) {
      currentMatchRef.current = match;
      setIsReady(false);
      loadMatchVideo(match);
      pendingSeekRef.current = vt;
    } else {
      seekToAndPlay(vt);
    }
  };

  const selectedClipIndex = selectedEvent ? filteredEvents.indexOf(selectedEvent) : -1;

  const goToPreviousClip = () => {
    if (selectedClipIndex > 0) doSelectEvent(filteredEvents[selectedClipIndex - 1]);
  };

  const goToNextClip = () => {
    if (selectedClipIndex >= 0 && selectedClipIndex < filteredEvents.length - 1) {
      doSelectEvent(filteredEvents[selectedClipIndex + 1]);
    }
  };

  const replayClip = () => {
    if (selectedEvent) doSelectEvent(selectedEvent);
  };

  const renderTitleBarButtons = (filter) =>
    filter.singleSelection === false && (
      <div className="relative z-10 flex gap-0.5 shrink-0">
        <button
          type="button"
          className="btn btn-ghost btn-xs btn-square min-h-0 h-5 w-5 p-0"
          title={t("Select All")}
          onClick={() => selectAllItems(filter, onFilterOptionChanged)}
        >
          <CheckIcon className="w-3 h-3" />
        </button>
        <button
          type="button"
          className="btn btn-ghost btn-xs btn-square min-h-0 h-5 w-5 p-0"
          title={t("Select None")}
          onClick={() => selectNoneItems(filter, onFilterOptionChanged)}
        >
          <XMarkIcon className="w-3 h-3" />
        </button>
      </div>
    );

  const renderFilterBox = (filter, defaultOpen = true, columns = 1) => (
    <div key={filter.title} tabIndex={0} className="collapse collapse-arrow rounded-sm border border-base-300 shadow-sm mb-1">
      <input type="checkbox" className="peer" defaultChecked={defaultOpen} />
      <div className="collapse-title min-h-0 py-1 bg-base-200 text-xs font-medium peer-checked:bg-base-100 peer-checked:text-base-700 flex items-center justify-between gap-1">
        <span className="truncate">{t(filter.title).toUpperCase()}</span>
        {renderTitleBarButtons(filter)}
      </div>
      <div className="collapse-content !min-h-0 !pt-0 max-h-48 overflow-y-auto peer-checked:bg-base-100">
        <MultiChoiceCompact filter={filter} handleOptionChanged={onFilterOptionChanged} showHeader={false} columns={columns} />
      </div>
    </div>
  );

  const renderSkillBox = (skillNo) => {
    const sf = skillFilters[skillNo];
    const dims = kSkillFilterDims[skillNo].filter((dim) => sf[kDimToFilterKey[dim]]);
    const enabled = enabledSkills.has(skillNo);
    return (
      <div
        key={skillNo}
        tabIndex={0}
        className={`collapse collapse-arrow rounded-sm border border-base-300 shadow-sm mb-1 ${enabled ? "" : "opacity-50"}`}
      >
        <input type="checkbox" className="peer" />
        <div className="collapse-title min-h-0 py-1 bg-base-200 text-xs font-medium peer-checked:bg-base-100 peer-checked:text-base-700 flex items-center justify-between gap-1">
          <span className="truncate">{t(kSkillLabels[skillNo])}</span>
          <input
            type="checkbox"
            className="toggle toggle-xs relative z-10 shrink-0"
            checked={enabled}
            onChange={() => toggleSkillEnabled(skillNo)}
            title={enabled ? t("Disable this skill") : t("Enable this skill")}
          />
        </div>
        <div className="collapse-content !min-h-0 !pt-0 peer-checked:bg-base-100 flex flex-col gap-2">
          {dims.map((dim, idx) => {
            const filter = sf[kDimToFilterKey[dim]];
            return (
              <div key={dim} className={idx === 0 ? "max-h-36 overflow-y-auto" : "border-t border-base-300 pt-1 max-h-36 overflow-y-auto"}>
                <MultiChoiceCompact
                  filter={filter}
                  handleOptionChanged={onFilterOptionChanged}
                  title={t(filter.title).toUpperCase()}
                  columns={kDimColumns[dim]}
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (!matches) {
    return (
      <div className="alert alert-error m-4">
        <span>{t("No match data available.")}</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-1rem)]">
      <div className="flex items-center justify-between px-2 py-1">
        <h2 className="font-bold text-lg">{t("Advanced Filters")}</h2>
        <div className="flex gap-2">
          <button type="button" className="btn btn-sm" onClick={resetAllFilters}>
            {t("Reset")}
          </button>
          <button type="button" className="btn btn-sm" onClick={() => navigate(-1)}>
            {t("Close")}
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 flex gap-2 px-2 pb-2">
        {/* Column 1: filters */}
        <div className="w-60 shrink-0 overflow-y-auto flex flex-col gap-2">
          <div>
            <p className="text-xs font-bold uppercase opacity-60 px-1 mb-1">{t("Teams & Players")}</p>
            {renderFilterBox(teamsFilter)}
            {renderFilterBox(playersFilter)}
          </div>
          <div>
            <div className="flex items-center justify-between px-1 mb-1">
              <p className="text-xs font-bold uppercase opacity-60">{t("Skills")}</p>
              <div className="flex gap-0.5">
                <button
                  type="button"
                  className="btn btn-ghost btn-xs btn-square min-h-0 h-5 w-5 p-0"
                  title={t("Select All")}
                  onClick={enableAllSkills}
                >
                  <CheckIcon className="w-3 h-3" />
                </button>
                <button
                  type="button"
                  className="btn btn-ghost btn-xs btn-square min-h-0 h-5 w-5 p-0"
                  title={t("Select None")}
                  onClick={disableAllSkills}
                >
                  <XMarkIcon className="w-3 h-3" />
                </button>
              </div>
            </div>
            {kTrackedSkills.map((sk) => renderSkillBox(sk))}
          </div>
          <div>
            <p className="text-xs font-bold uppercase opacity-60 px-1 mb-1">{t("Match State")}</p>
            {renderFilterBox(setsFilter, true, 5)}
            {renderFilterBox(rotationsFilter, true, 3)}
            {renderFilterBox(phasesFilter, true, 2)}
            {renderFilterBox(scoreRangeFilter)}
            {renderFilterBox(setWinnerFilter)}
            {renderFilterBox(jumpHeightFilter)}
            <div className="rounded-sm border border-base-300 shadow-sm mb-1 p-1">
              <label className="text-xs font-medium block mb-0.5" htmlFor="customCodeSearch">
                {t("Custom Code")}
              </label>
              <input
                id="customCodeSearch"
                type="text"
                className="input input-xs input-bordered w-full"
                placeholder={t("Search... (comma-separated for multiple)")}
                value={customCodeSearch}
                onChange={(e) => setCustomCodeSearch(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Column 2: events list (same component Video Analysis uses) */}
        <div className="w-60 shrink-0 flex flex-col gap-1">
          <div className="flex justify-end gap-1 shrink-0">
            <button
              type="button"
              className="btn btn-xs btn-square"
              title={collapseAll ? t("Expand All") : t("Collapse All")}
              onClick={() => setCollapseAll((v) => !v)}
            >
              {collapseAll ? <ChevronDoubleDownIcon className="w-4 h-4" /> : <ChevronDoubleUpIcon className="w-4 h-4" />}
            </button>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto border border-base-300 rounded-md">
            <EventsList
              matches={matches}
              team={team}
              filterFn={passesEvent}
              selectedSet={undefined}
              selectedMatchGuid={undefined}
              doSelectEvent={doSelectEvent}
              onFilter={setFilteredEvents}
              collapseAll={collapseAll}
              selectedEvent={selectedEvent}
            />
          </div>
        </div>

        {/* Column 3: video player for the currently selected event */}
        <div className="flex-1 min-w-0 flex flex-col gap-2">
          {selectedEvent && (
            <div className="text-xs text-base-content/80 px-1 flex flex-wrap items-center gap-x-3 gap-y-0.5">
              {selectedEvent.Player && (
                <span className="font-semibold">
                  {selectedEvent.Player.shirtNumber}. {selectedEvent.Player.NickName?.toUpperCase()}
                </span>
              )}
              <span>{DVEventString(selectedEvent) ?? selectedEvent.EventString ?? ""}</span>
              {selectedEvent.Drill?.GameNumber != null && (
                <span>
                  {t("Set")} {selectedEvent.Drill.GameNumber}
                </span>
              )}
              <span>
                {selectedEvent.TeamScore ?? 0}-{selectedEvent.OppositionScore ?? 0}
              </span>
              {matches.length > 1 && selectedEvent.Drill?.match && (
                <span className="opacity-70">
                  {selectedEvent.Drill.match.teamA?.Name} {t("vs")} {selectedEvent.Drill.match.teamB?.Name}
                </span>
              )}
            </div>
          )}
          <div className="aspect-video bg-black rounded-md overflow-hidden">
            {videoFilePath ? (
              videoFilePath.endsWith(".m3u8") ? (
                <video
                  ref={hlsVideoRef}
                  controls
                  onCanPlay={handlePlayerReady}
                  className="w-full h-full"
                  crossOrigin="anonymous"
                />
              ) : (
                <ReactPlayer
                  ref={playerRef}
                  url={videoFilePath}
                  playing={isPlaying}
                  width="100%"
                  height="100%"
                  controls
                  onReady={handlePlayerReady}
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                />
              )
            ) : (
              <div className="flex items-center justify-center h-full text-base-content/50 text-sm">
                {t("No video available for this match.")}
              </div>
            )}
          </div>
          {videoFilePath && (
            <div className="flex items-center justify-center gap-1">
              <button type="button" className="btn btn-xs btn-square" title={t("Rewind 60 seconds")} onClick={() => seekRelative(-60)}>
                <BackwardIcon className="w-4 h-4" />
              </button>
              <button type="button" className="btn btn-xs btn-square" title={t("Rewind 10 seconds")} onClick={() => seekRelative(-10)}>
                <ChevronDoubleLeftIcon className="w-4 h-4" />
              </button>
              <button type="button" className="btn btn-xs btn-square" title={t("Rewind 1 second")} onClick={() => seekRelative(-1)}>
                <ChevronLeftIcon className="w-4 h-4" />
              </button>
              <button type="button" className="btn btn-xs btn-square" title={t("Rewind 1 frame")} onClick={() => seekRelative(-FRAME_SECONDS)}>
                <ChevronLeftMiniIcon className="w-4 h-4" />
              </button>
              <button type="button" className="btn btn-xs btn-square" title={t("Forward 1 frame")} onClick={() => seekRelative(FRAME_SECONDS)}>
                <ChevronRightMiniIcon className="w-4 h-4" />
              </button>
              <button type="button" className="btn btn-xs btn-square" title={t("Forward 1 second")} onClick={() => seekRelative(1)}>
                <ChevronRightIcon className="w-4 h-4" />
              </button>
              <button type="button" className="btn btn-xs btn-square" title={t("Forward 10 seconds")} onClick={() => seekRelative(10)}>
                <ChevronDoubleRightIcon className="w-4 h-4" />
              </button>
              <button type="button" className="btn btn-xs btn-square" title={t("Forward 60 seconds")} onClick={() => seekRelative(60)}>
                <ForwardIcon className="w-4 h-4" />
              </button>
            </div>
          )}
          {videoFilePath && (
            <div className="flex items-center justify-center gap-1">
              <button
                type="button"
                className="btn btn-xs"
                title={t("Previous Clip")}
                disabled={selectedClipIndex <= 0}
                onClick={goToPreviousClip}
              >
                <ArrowLeftCircleIcon className="w-4 h-4" />
                {t("Previous Clip")}
              </button>
              <button
                type="button"
                className="btn btn-xs"
                title={t("Replay")}
                disabled={!selectedEvent}
                onClick={replayClip}
              >
                <ArrowPathIcon className="w-4 h-4" />
                {t("Replay")}
              </button>
              <button
                type="button"
                className="btn btn-xs"
                title={t("Next Clip")}
                disabled={selectedClipIndex < 0 || selectedClipIndex >= filteredEvents.length - 1}
                onClick={goToNextClip}
              >
                <ArrowRightCircleIcon className="w-4 h-4" />
                {t("Next Clip")}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default VideoAnalysisFiltersPage;
